<template>
	<div class="d3Model">
	  <div ref="d3Model" class="d3" id="d3Model"></div>
	</div>
  </template>
  
  <script>
  import * as THREE from 'three';
  import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
  import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
  import Stats from 'three/examples/jsm/libs/stats.module.js';
  
  export default {
	data() {
	  return {
		scene: null,
		camera: null,
		renderer: null,
		controls: null,
		stats: null,
		animationFrameId: null,
	  };
	},
	mounted() {
	  const fileExtension = this.$route.query.src.split('.').pop();
	  if (fileExtension === 'fbx') {
		this.$nextTick(() => {
		  this.init();
		  this.animate();
		});
	  } else {
		this.$message.warning('文件类型只支持 fbx 文件格式');
	  }
	},
	beforeUnmount() {
	  // 清理资源
	  this.cleanup();
	},
	methods: {
	  init() {
		// 初始化场景
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color(0xa0a0a0);
		this.scene.fog = new THREE.Fog(0xa0a0a0, 200, 1000);
  
		// 初始化相机
		this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 2000);
		this.camera.position.set(0, 1.6, 0);
  
		// 初始化渲染器
		this.renderer = new THREE.WebGLRenderer({ antialias: true });
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.shadowMap.enabled = true;
		this.$refs.d3Model.appendChild(this.renderer.domElement);
  
		// 初始化光源
		const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444);
		hemisphereLight.position.set(0, 20, 0);
		this.scene.add(hemisphereLight);
  
		const directionalLight = new THREE.DirectionalLight(0xffffff);
		directionalLight.position.set(0, 20, 10);
		directionalLight.castShadow = true;
		this.scene.add(directionalLight);
  
		// 添加网格辅助线
		const grid = new THREE.GridHelper(5, 20, 0x000000, 0x000000);
		grid.material.opacity = 0.2;
		grid.material.transparent = true;
		this.scene.add(grid);
  
		// 初始化控制器
		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.target.set(0, 0, 0);
		this.controls.update();
  
		// 初始化性能统计
		this.stats = new Stats();
		this.$refs.d3Model.appendChild(this.stats.dom);
  
		// 加载模型
		const loader = new FBXLoader();
		loader.load(
		  this.$route.query.src,
		  (object) => {
			object.traverse((child) => {
			  if (child.isMesh) {
				child.material = new THREE.MeshStandardMaterial({
					color: 0xa0a0a0,
					metalness: 0.1,
					roughness: 0.5,
				});
				child.castShadow = true;
				child.receiveShadow = true;
			  }
			});
			object.rotation.y = Math.PI / 2;
			object.position.set(0, -0.15, 0.3);
			object.scale.set(0.003, 0.003, 0.003);
			this.scene.add(object);
		  },
		  (xhr) => {
			console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
		  },
		  (error) => {
			console.error('Error loading FBX file:', error);
		  }
		);
  
		// 监听窗口大小变化
		window.addEventListener('resize', this.onWindowResize);
	  },
	  onWindowResize() {
		this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(window.innerWidth, window.innerHeight);
	  },
	  animate() {
		this.animationFrameId = requestAnimationFrame(this.animate);
		this.renderer.render(this.scene, this.camera);
		this.stats.update();
	  },
	  cleanup() {
		// 停止渲染循环
		if (this.animationFrameId) {
		  cancelAnimationFrame(this.animationFrameId);
		}
  
		// 移除事件监听器
		window.removeEventListener('resize', this.onWindowResize);
  
		// 清理渲染器
		if (this.renderer) {
		  this.renderer.dispose();
		}
  
		// 清理场景
		if (this.scene) {
		  this.scene.traverse((object) => {
			if (object.isMesh) {
			  object.geometry.dispose();
			  if (object.material) {
				if (Array.isArray(object.material)) {
				  object.material.forEach((material) => material.dispose());
				} else {
				  object.material.dispose();
				}
			  }
			}
		  });
		}
  
		// 移除 DOM 元素
		if (this.$refs.d3Model) {
		  this.$refs.d3Model.removeChild(this.renderer.domElement);
		  this.$refs.d3Model.removeChild(this.stats.dom);
		}
	  },
	},
  };
  </script>
  
  <style lang="scss" scoped>
  .d3Model {
	width: 100%;
	height: 100vh;
	.d3 {
	  width: 100%;
	  height: 100%;
	}
  }
  </style>